import { useMemo, useState, type ReactNode } from 'react';
import {
  Check, ChevronDown, ChevronRight, MessageCircleWarning, Pencil, Plus, PlusCircle, Quote, RotateCcw, Trash2,
} from 'lucide-react';
import { CitationMarginCard } from '../kb/CitationPanel';
import { Button } from '../ui/button';
import { Textarea } from '../ui/input';
import { matchQuoteLine, NewLineWords, OldLineWords, type DiffLine } from '../ui/diff';
import type { KbChangeComment, KbCitation } from '../../lib/api';
import { cn } from '../../lib/utils';
import { sectionLabel, sectionSubtitle, type DiffSection } from './review-model';
import type { ChangeReview } from './useChangeReview';

/** Changed sections open by default; past this many, the rest start folded so a long diff scans. */
const OPEN_BY_DEFAULT = 3;
/** Unchanged lines kept beside a change inside a section. */
const CONTEXT = 1;

function Counts({ added, removed }: { added: number; removed: number }) {
  return (
    <span className="flex items-center gap-1.5 text-[11px] font-semibold tabular-nums flex-shrink-0">
      {removed > 0 && <span className="text-red-600 dark:text-red-400">−{removed}</span>}
      {added > 0 && <span className="text-green-600 dark:text-green-400">+{added}</span>}
    </span>
  );
}

/** One pinned comment beneath its line — the classic card's note, in this view's type scale. */
function CommentNote({ comment, onDelete }: { comment: KbChangeComment; onDelete?: () => void }) {
  return (
    <div className="flex items-start gap-2">
      <MessageCircleWarning
        size={14}
        className={cn('mt-0.5 flex-shrink-0', comment.status === 'DRAFT' ? 'text-amber-600 dark:text-amber-500' : 'text-ink-faint')}
      />
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] leading-relaxed text-ink-secondary whitespace-pre-wrap">{comment.text}</p>
        <p className="text-[11px] text-ink-faint mt-0.5">
          {comment.status === 'DRAFT' ? 'Not sent yet — goes to the agent with your feedback' : 'Sent to a fix run'}
          {comment.onRemoved ? ' · on text this change removed' : ''}
        </p>
      </div>
      {onDelete && (
        <button onClick={onDelete} title="Discard this comment" className="text-red-500 hover:text-red-700 flex-shrink-0">
          <Trash2 size={11} />
        </button>
      )}
    </div>
  );
}

/** The classic diff's comment stripe, class for class, so a comment looks the same in either view. */
function Stripe({ children }: { children: ReactNode }) {
  return (
    <div className="border-y border-amber-300/80 dark:border-amber-700/50 border-l-[3px] border-l-amber-500 bg-amber-100/70 dark:bg-amber-900/25 px-3.5 py-2.5 font-sans space-y-2 shadow-inner">
      {children}
    </div>
  );
}

type Row =
  | { kind: 'line'; index: number }
  | { kind: 'gap'; count: number };

/**
 * The exact changes, section by section.
 *
 * A layout over the same line diff the classic view renders, not a second diff: every line is still
 * addressed by its verbatim text, so review comments land beneath the line they quote (and in the
 * section that holds it), evidence finds its line through the same `matchQuoteLine`, and the fix run
 * receives exactly the quotes it always did. What is new is the grouping — a reviewer reads "the
 * pricing section was rewritten", not "lines 40–71" — and line-level editing.
 */
export function SectionDiff({ review }: { review: ChangeReview }) {
  const { change, diff, citations } = review;
  const [openState, setOpenState] = useState<Record<string, boolean>>({});
  const [fullSections, setFullSections] = useState<Record<string, boolean>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [editingLine, setEditingLine] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [openEvidence, setOpenEvidence] = useState<number | null>(null);

  const lines = diff?.lines ?? [];

  /** Line index → the comments (and composer) pinned to it; whatever matches nothing is orphaned. */
  const { pinned, orphaned, composerAt } = useMemo(() => {
    const pinned = new Map<number, KbChangeComment[]>();
    const orphaned: KbChangeComment[] = [];
    const find = (quote: string, removed: boolean) =>
      lines.findIndex((l) => l.text === quote && (removed ? l.type === 'remove' : l.type !== 'remove'));
    for (const comment of review.comments ?? []) {
      if (!comment.quote) continue;
      const index = find(comment.quote, comment.onRemoved);
      if (index === -1) orphaned.push(comment);
      else pinned.set(index, [...(pinned.get(index) ?? []), comment]);
    }
    const composerAt = review.composer ? find(review.composer.quote, review.composer.removed) : -1;
    return { pinned, orphaned, composerAt };
  }, [lines, review.comments, review.composer]);

  /** Line index → current evidence for it. Superseded evidence has no line by definition. */
  const { evidenceAt, unplaced } = useMemo(() => {
    const evidenceAt = new Map<number, KbCitation[]>();
    const unplaced: KbCitation[] = [];
    for (const citation of citations?.current ?? []) {
      if (!citation.nodeQuote) continue;
      const index = matchQuoteLine(lines, citation.nodeQuote);
      if (index === -1) unplaced.push(citation);
      else evidenceAt.set(index, [...(evidenceAt.get(index) ?? []), citation]);
    }
    return { evidenceAt, unplaced };
  }, [lines, citations]);

  if (!diff) return null;

  const changed = diff.sections.filter((s) => s.status !== 'unchanged');
  const unchanged = diff.sections.filter((s) => s.status === 'unchanged');
  const titleChanged = Boolean(change.beforeTitle && change.afterTitle && change.beforeTitle !== change.afterTitle);

  const holdsWork = (section: DiffSection) =>
    section.lineIndexes.some((i) => pinned.has(i) || i === composerAt || i === editingLine);

  const isOpen = (section: DiffSection, defaultOpen: boolean) => {
    // A section holding the open composer or line editor cannot fold away under the reviewer.
    if (section.lineIndexes.includes(composerAt) || (editingLine !== null && section.lineIndexes.includes(editingLine))) return true;
    return openState[section.key] ?? (defaultOpen || holdsWork(section));
  };

  const rowsOf = (section: DiffSection): Row[] => {
    if (fullSections[section.key] || section.status === 'unchanged') {
      return section.lineIndexes.map((index) => ({ kind: 'line', index }));
    }
    const keep = new Set<number>();
    section.lineIndexes.forEach((index, pos) => {
      const line = lines[index]!;
      if (line.type !== 'same' || pinned.has(index) || index === composerAt) {
        for (let k = Math.max(0, pos - CONTEXT); k <= Math.min(section.lineIndexes.length - 1, pos + CONTEXT); k++) {
          keep.add(section.lineIndexes[k]!);
        }
      }
    });
    // The heading line is the section's own name, already in its header — unless it was rewritten,
    // where the before/after pair is the change. Blank lines carry nothing to review either way.
    const rows: Row[] = [];
    let hidden = 0;
    section.lineIndexes.forEach((index, pos) => {
      const line = lines[index]!;
      const isHeadingLine =
        pos === 0 && section.heading !== null && !section.renamedFrom && !pinned.has(index) && index !== composerAt;
      const isBlank = line.text.trim() === '';
      if (isHeadingLine || isBlank) return;
      if (keep.has(index)) {
        if (hidden > 0) rows.push({ kind: 'gap', count: hidden });
        hidden = 0;
        rows.push({ kind: 'line', index });
      } else {
        hidden++;
      }
    });
    if (hidden > 0) rows.push({ kind: 'gap', count: hidden });
    return rows;
  };

  const startLineEdit = async (line: DiffLine, index: number) => {
    if (!(await review.ensureLineEditable())) return;
    const newIndex = line.newLineNo! - 1;
    review.setLineEdits((prev) => (prev[newIndex] === undefined ? { ...prev, [newIndex]: line.text } : prev));
    setEditingLine(index);
  };

  const addLine = async (section: DiffSection) => {
    if (section.lastNewIndex === null || !(await review.ensureLineEditable())) return;
    const at = section.lastNewIndex;
    review.setLineInserts((prev) => ({ ...prev, [at]: [...(prev[at] ?? []), ''] }));
  };

  const renderLine = (index: number) => {
    const line = lines[index]!;
    const newIndex = line.newLineNo !== null ? line.newLineNo - 1 : null;
    const edited = newIndex !== null ? review.lineEdits[newIndex] : undefined;
    const evidence = evidenceAt.get(index) ?? [];
    const comments = pinned.get(index) ?? [];
    const isBlank = line.text.trim() === '';

    // The classic diff's row tints: the background carries the change, the text stays readable ink.
    const tone =
      line.type === 'add'
        ? 'bg-green-50 dark:bg-green-950/30 text-ink-secondary'
        : line.type === 'remove'
          ? 'bg-red-50 dark:bg-red-950/40 text-red-950 dark:text-red-100'
          : 'text-ink-secondary';

    return (
      <div key={`l-${index}`}>
        <div className={cn('group relative flex items-start', tone)}>
          <span
            className={cn(
              'w-6 flex-shrink-0 text-center select-none font-semibold py-0.5',
              line.type === 'add' && 'text-green-600 dark:text-green-400',
              line.type === 'remove' && 'text-red-600 dark:text-red-400',
            )}
          >
            {line.type === 'add' ? '+' : line.type === 'remove' ? '−' : ''}
          </span>

          <div className="flex-1 min-w-0 py-0.5 pr-2 whitespace-pre-wrap break-words">
            {editingLine === index && newIndex !== null ? (
              <div className="space-y-1.5 py-0.5">
                <Textarea
                  value={edited ?? line.text}
                  onChange={(e) => review.setLineEdits((prev) => ({ ...prev, [newIndex]: e.target.value }))}
                  rows={Math.min(8, Math.max(2, Math.ceil((edited ?? line.text).length / 90)))}
                  autoFocus
                  className="text-[12.5px] font-mono"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    onClick={() => {
                      review.setLineEdits(({ [newIndex]: _drop, ...rest }) => rest);
                      setEditingLine(null);
                    }}
                  >
                    Revert line
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => setEditingLine(null)}>
                    <Check size={12} />Done
                  </Button>
                </div>
              </div>
            ) : edited !== undefined ? (
              <span className="block">
                {edited === '' ? (
                  <span className="italic text-ink-faint line-through">{line.text}</span>
                ) : (
                  <span className="text-blue-800 dark:text-blue-300">{edited}</span>
                )}
                <span className="ml-2 text-[10px] font-sans font-medium text-blue-600 dark:text-blue-400">
                  {edited === '' ? 'you removed this line' : 'your edit'}
                </span>
              </span>
            ) : line.wordTokens ? (
              line.type === 'remove' ? <OldLineWords tokens={line.wordTokens} /> : <NewLineWords tokens={line.wordTokens} />
            ) : isBlank ? (
              ' '
            ) : (
              line.text
            )}
          </div>

          {editingLine !== index && (
            <div className="flex items-center gap-1 flex-shrink-0 pr-2 pt-0.5 font-sans">
              {evidence.length > 0 && (
                <button
                  onClick={() => setOpenEvidence((cur) => (cur === index ? null : index))}
                  title="The source behind this line"
                  className={cn(
                    'inline-flex items-center gap-1 rounded px-1 h-[18px] text-[10px] font-semibold transition-colors',
                    openEvidence === index
                      ? 'bg-orange-500 text-white'
                      : 'bg-surface-muted text-ink-muted hover:text-orange-600',
                  )}
                >
                  <Quote size={9} />
                  {evidence.length}
                </button>
              )}
              {review.canReview && !isBlank && (
                <button
                  onClick={() => review.openComposer({ quote: line.text, removed: line.type === 'remove' })}
                  title="Comment on this line"
                  aria-label="Comment on this line"
                  className="flex h-[18px] w-[18px] items-center justify-center rounded bg-blue-600 text-white shadow-sm opacity-0 transition-all duration-100 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-blue-500"
                >
                  <Plus size={13} strokeWidth={3} />
                </button>
              )}
              {review.canEditLines && newIndex !== null && !isBlank && (
                <button
                  onClick={() => startLineEdit(line, index)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 h-[18px] text-[10px] font-medium text-ink-secondary hover:text-ink hover:border-border-strong transition-all',
                    line.type === 'add' || edited !== undefined ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                  )}
                >
                  <Pencil size={9} />Edit
                </button>
              )}
            </div>
          )}
        </div>

        {openEvidence === index && evidence.length > 0 && (
          <div className="px-6 py-2 grid gap-1.5 sm:grid-cols-2 bg-surface-subtle border-y border-border-subtle font-sans">
            {evidence.map((c) => (
              <CitationMarginCard key={c.id} citation={c} onOpen={review.setSelectedCitation} />
            ))}
          </div>
        )}

        {(comments.length > 0 || composerAt === index) && (
          <Stripe>
            {comments.map((c) => (
              <CommentNote
                key={c.id}
                comment={c}
                onDelete={review.canReview && c.status === 'DRAFT' ? () => review.removeComment(c.id) : undefined}
              />
            ))}
            {composerAt === index && (
              <div className="space-y-1.5">
                <Textarea
                  value={review.commentText}
                  onChange={(e) => review.setCommentText(e.target.value)}
                  rows={2}
                  autoFocus
                  className="text-[12.5px]"
                  placeholder="What's wrong here, or what should it say?"
                />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" disabled={review.commentBusy} onClick={() => review.setComposer(null)}>Cancel</Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={review.commentBusy || review.commentText.trim().length < 3}
                    onClick={review.addComment}
                  >
                    <Check size={12} />Add comment
                  </Button>
                </div>
              </div>
            )}
          </Stripe>
        )}
      </div>
    );
  };

  const renderInserts = (section: DiffSection) => {
    if (section.lastNewIndex === null) return null;
    const at = section.lastNewIndex;
    const inserted = review.lineInserts[at] ?? [];
    return inserted.map((text, k) => (
      <div key={`ins-${at}-${k}`} className="flex items-start gap-0 py-1 pr-2 bg-blue-50 dark:bg-blue-950/30">
        <span className="w-6 flex-shrink-0 text-center font-semibold text-blue-600 pt-1">+</span>
        <Textarea
          value={text}
          onChange={(e) =>
            review.setLineInserts((prev) => ({ ...prev, [at]: (prev[at] ?? []).map((t, j) => (j === k ? e.target.value : t)) }))
          }
          rows={2}
          autoFocus={text === ''}
          placeholder="A new line for this section…"
          className="flex-1 text-[12.5px] font-mono"
        />
        <button
          onClick={() => review.setLineInserts((prev) => ({ ...prev, [at]: (prev[at] ?? []).filter((_, j) => j !== k) }))}
          title="Remove this new line"
          className="mt-1.5 text-ink-faint hover:text-red-500"
        >
          <Trash2 size={13} />
        </button>
      </div>
    ));
  };

  const renderSection = (section: DiffSection, defaultOpen: boolean) => {
    const open = isOpen(section, defaultOpen);
    const notes = section.lineIndexes.reduce((n, i) => n + (pinned.get(i)?.length ?? 0), 0);
    return (
      <div key={section.key} className="rounded-md border border-border bg-card overflow-hidden">
        <button
          onClick={() => setOpenState((prev) => ({ ...prev, [section.key]: !open }))}
          className="w-full flex items-start gap-2 px-3 py-2 text-left bg-surface-subtle hover:bg-surface-muted transition-colors"
        >
          {open ? (
            <ChevronDown size={13} className="mt-0.5 text-ink-faint flex-shrink-0" />
          ) : (
            <ChevronRight size={13} className="mt-0.5 text-ink-faint flex-shrink-0" />
          )}
          <span className="flex-1 min-w-0">
            <span className="block text-xs font-semibold text-ink truncate">{sectionLabel(section)}</span>
            <span className="block text-[11px] text-ink-muted">{sectionSubtitle(section)}</span>
          </span>
          {notes > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5">
              <MessageCircleWarning size={11} />{notes}
            </span>
          )}
          <span className="mt-0.5"><Counts added={section.added} removed={section.removed} /></span>
        </button>
        {open && (
          <div className="border-t border-border-subtle font-mono text-[12.5px] leading-[1.65]">
            {rowsOf(section).map((row, k) =>
              row.kind === 'gap' ? (
                <button
                  key={`g-${k}`}
                  onClick={() => setFullSections((prev) => ({ ...prev, [section.key]: true }))}
                  className="w-full text-left px-6 py-0.5 font-sans text-[11px] bg-blue-50 dark:bg-blue-950/40 text-ink-faint dark:text-blue-300/60 hover:text-ink transition-colors"
                >
                  ··· {row.count} unchanged {row.count === 1 ? 'line' : 'lines'}
                </button>
              ) : (
                renderLine(row.index)
              ),
            )}
            {renderInserts(section)}
            {review.canEditLines && section.lastNewIndex !== null && (
              <button
                onClick={() => addLine(section)}
                className="w-full flex items-center gap-1.5 px-3 py-1.5 border-t border-border-subtle font-sans text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:bg-surface-subtle"
              >
                <PlusCircle size={12} />Add a line
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {titleChanged && (
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="flex items-start gap-2 px-3 py-2 bg-surface-subtle">
            <span className="flex-1 min-w-0 pl-5">
              <span className="block text-xs font-semibold text-ink">Title</span>
              <span className="block text-[11px] text-ink-muted">Renamed.</span>
            </span>
            <Counts added={1} removed={1} />
          </div>
          <div className="border-t border-border-subtle font-mono text-[12.5px] leading-[1.65]">
            <div className="flex items-start bg-red-50 dark:bg-red-950/40 text-red-950 dark:text-red-100">
              <span className="w-6 flex-shrink-0 text-center py-0.5 font-semibold text-red-600 dark:text-red-400">−</span>
              <span className="flex-1 py-0.5 pr-2">{change.beforeTitle}</span>
            </div>
            <div className="group flex items-start bg-green-50 dark:bg-green-950/30 text-ink-secondary">
              <span className="w-6 flex-shrink-0 text-center py-0.5 font-semibold text-green-600 dark:text-green-400">+</span>
              {editingTitle ? (
                <div className="flex-1 flex gap-1.5 py-1 pr-2 font-sans">
                  <input
                    value={review.titleEdit ?? change.afterTitle ?? ''}
                    onChange={(e) => review.setTitleEdit(e.target.value)}
                    autoFocus
                    className="flex-1 rounded-md border border-border bg-card px-2 py-1 text-xs text-ink"
                  />
                  <Button size="sm" onClick={() => { review.setTitleEdit(null); setEditingTitle(false); }} title="Revert">
                    <RotateCcw size={12} />
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => setEditingTitle(false)} title="Done">
                    <Check size={12} />
                  </Button>
                </div>
              ) : (
                <>
                  <span className={cn('flex-1 py-0.5 pr-2', review.titleEdit !== null && 'text-blue-800 dark:text-blue-300')}>
                    {review.titleEdit ?? change.afterTitle}
                    {review.titleEdit !== null && (
                      <span className="ml-2 font-sans text-[10px] font-medium text-blue-600 dark:text-blue-400">your edit</span>
                    )}
                  </span>
                  {review.canEditLines && (
                    <button
                      onClick={async () => { if (await review.ensureLineEditable()) setEditingTitle(true); }}
                      className="mr-2 mt-0.5 inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 h-[18px] font-sans text-[10px] font-medium text-ink-secondary hover:text-ink"
                    >
                      <Pencil size={9} />Edit
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* A removed section starts folded: its subtitle already says what happened to it, and a wall
          of red lines is the part of a diff people skim past to find what is still on the page. */}
      {changed.map((section, i) =>
        renderSection(section, section.status !== 'removed' && (changed.length <= OPEN_BY_DEFAULT || i < OPEN_BY_DEFAULT)),
      )}

      {changed.length === 0 && !titleChanged && (
        <p className="text-xs text-ink-faint px-1">No text changed.</p>
      )}

      {unchanged.length > 0 && (
        <>
          <button
            onClick={() => setShowUnchanged((v) => !v)}
            className="px-1 text-[11px] text-ink-faint hover:text-ink transition-colors"
          >
            {showUnchanged ? 'Hide' : 'Show'} {unchanged.length} unchanged {unchanged.length === 1 ? 'section' : 'sections'}
          </button>
          {showUnchanged && unchanged.map((section) => renderSection(section, false))}
        </>
      )}

      {orphaned.length > 0 && (
        <div className="rounded-md border border-amber-300/80 dark:border-amber-700/50 border-l-[3px] border-l-amber-500 bg-amber-100/70 dark:bg-amber-900/25 px-3.5 py-2.5 space-y-2">
          <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-500 uppercase tracking-[0.07em]">
            Comments on lines no longer in this change
          </p>
          {orphaned.map((c) => (
            <CommentNote
              key={c.id}
              comment={c}
              onDelete={review.canReview && c.status === 'DRAFT' ? () => review.removeComment(c.id) : undefined}
            />
          ))}
        </div>
      )}

      {unplaced.length > 0 && (
        <div className="rounded-md border border-border-subtle bg-surface-subtle px-3.5 py-2.5">
          <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-2">Evidence for text not shown above</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {unplaced.map((c) => (
              <CitationMarginCard key={c.id} citation={c} onOpen={review.setSelectedCitation} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
